import {
  Button,
  Card,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Form, Formik, Field, FastField } from "formik";
import { TextField, Select } from "formik-mui";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { sendContactSales } from "../api/api";
import ct from "countries-and-timezones";
import Box from "@mui/material/Box";
import { useCallback, useState } from "react";
import * as Yup from "yup";
import MuiPhoneNumber from "material-ui-phone-number-2";
import phone from "phone";
import { useProfile } from "../api/hooks";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useSnackbar } from "../store/store";
import { useMedia } from "react-use";

export const ContactSales = () => {
  const navigate = useNavigate();

  const { profile, isLoading } = useProfile();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const isDarkTheme = useMedia("(prefers-color-scheme: dark)");
  const countries = ct.getAllCountries();

  const snackbar = useSnackbar();
  const alert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const handleClickOpen = () => {
    setConfirmOpen(true);
  };

  const handleClose = () => {
    setConfirmOpen(false);
  };

  const isSuperAdmin =
    profile?.roles?.some((role) => role.type === "SUPER_ADMIN") ?? false;
  const isAdmin =
    (profile?.roles?.some((role) => role.type === "ADMIN") || isSuperAdmin) ??
    false;

  const {
    mutateAsync,
    error: mutateError,
    data: mutateData,
    isLoading: mutateIsLoading,
  } = useMutation("sendContactSales", sendContactSales, {
    onSuccess: () => {
      navigate("/");
    },
    onError: (err: any) => {
      alert("Error", err.message);
    },
  });

  const handlePhoneChange = useCallback(
    (originalHandler) => (value) => {
      originalHandler({ target: { name: "phone", value } });
    },
    []
  );

  const HeaderContent = () => {
    return (
      <Box>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            margin: { xs: "12px 0", md: "0 0 24px" },
            fontSize: { xs: "2.8rem", md: "3.5rem" },
          }}
        >
          Contact our sales&nbsp;team
        </Typography>
        <Typography sx={{ mt: "32px" }}>With Spidipay you can:</Typography>
        <List
          dense={true}
          sx={{
            mt: 0,
          }}
        >
          <ListItem dense>
            <ListItemIcon sx={{ margin: 0 }}>
              <CheckIcon />
            </ListItemIcon>
            <ListItemText>
              Focus critical developer resources on your core business
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ margin: 0 }}>
              <CheckIcon />
            </ListItemIcon>
            <ListItemText>
              Launch new products faster with minimal payments code
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ margin: 0 }}>
              <CheckIcon />
            </ListItemIcon>
            <ListItemText>
              Improve conversion from international customers
            </ListItemText>
          </ListItem>
        </List>
      </Box>
    );
  };

  return (
    <div className="flex flex-col gap-2 justify-center items-center w-full">
      <>
        <Dialog
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{`${alertTitle}`}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {`${alertMessage}`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAlertOpen(false)}>OK</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmOpen}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Deletion confirmation"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {`The user will not be deleted if it is the owner of an business. Are you sure you want to delete this?`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            maxWidth: "920px",
            gap: "32px",
            margin: {
              xs: "0px auto 0",
              md: "24px auto 0",
              lg: "48px auto 0",
            },
          }}
        >
          <Box sx={{ display: { xs: "none", md: "block" }, mt: "32px" }}>
            <HeaderContent />
          </Box>
          <Card
            sx={{
              width: "100%",
              maxWidth: { xs: "450px", md: "420px" },
              borderWidth: isDarkTheme ? "0px" : "1px",
              padding: { xs: "16px 16px 24px", sm: "42px 32px 24px" },
              margin: "0 auto",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: "16px", md: "48px" },
              alignItems: "top",
              justifyContent: "center",
            }}
          >
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              <HeaderContent />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                position: "relative",
                flexGrow: 1,
                flexShrink: 0,
                width: "100%",
              }}
            >
              <Formik
                validateOnMount={true}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                initialValues={{
                  givenName: "",
                  familyName: "",
                  email: "",
                  phone: "",
                  website: "",
                  companySize: "",
                  country: "",
                  paymentsVolume: "",
                  message: "",
                }}
                validationSchema={Yup.object().shape({
                  givenName: Yup.string()
                    .min(2, "Too short")
                    .max(50, "Too long")
                    .required("Required"),
                  familyName: Yup.string()
                    .min(2, "Too short")
                    .max(50, "Too long")
                    .required("Required"),
                  email: Yup.string()
                    .email("Invalid email")
                    .required("Required"),
                  phone: Yup.string()
                    .test("phone", "Invalid phone number", (value) => {
                      if (!value) return true;
                      return phone(value).isValid;
                    })
                    .required("Required"),
                })}
                onSubmit={async (values, { setSubmitting }) => {
                  console.log(values);

                  try {
                    const res = await mutateAsync({
                      values,
                    });
                    const success = res.data;
                    if (success) {
                      snackbar.open({
                        type: "success",
                        message: `Contact form sent successfully`,
                      });
                    } else {
                      snackbar.open({
                        type: "error",
                        message: `Contact form was not sent`,
                      });
                    }
                  } catch (error: any) {
                    alert(error.error, error.message);
                  }

                  setSubmitting(false);
                }}
              >
                {({
                  submitForm,
                  isSubmitting,
                  values,
                  isValid,
                  handleChange,
                  errors,
                  touched,
                  handleBlur,
                }) => (
                  <Form style={{ width: "100%" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Field
                        component={TextField}
                        label="Given name"
                        name="givenName"
                        size="small"
                        required
                      />
                      <Field
                        component={TextField}
                        label="Family name"
                        name="familyName"
                        size="small"
                        required
                      />
                      <Field
                        component={TextField}
                        label="Work email"
                        name="email"
                        size="small"
                        required
                      />
                      <MuiPhoneNumber
                        required
                        size="small"
                        defaultCountry={"rw"}
                        name="phone"
                        label="Work phone"
                        variant="outlined"
                        value={values.phone}
                        onBlur={handleBlur}
                        onChange={handlePhoneChange(handleChange)}
                        error={touched.phone && !!errors.phone}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        //@ts-ignore
                        helperText={touched.phone && errors.phone}
                        className="MuiPhoneNumber"
                        InputLabelProps={{ required: false }}
                      />
                      <Field
                        component={TextField}
                        label="Company website"
                        name="website"
                        size="small"
                      />
                      <Box className="small-formik-select-fix">
                        <Field
                          component={Select}
                          label="Company size"
                          name="companySize"
                          size="small"
                          color="primary"
                        >
                          <MenuItem value={"1-99"}>1-99</MenuItem>
                          <MenuItem value={"100-999"}>100-999</MenuItem>
                          <MenuItem value={"1000-4999"}>1000-4999</MenuItem>
                          <MenuItem value={"5000+"}>5000+</MenuItem>
                        </Field>
                      </Box>
                      <Box className="small-formik-select-fix">
                        <FastField
                          component={Select}
                          label="Country"
                          id="country"
                          name="country"
                          size="small"
                          required
                        >
                          {!!countries &&
                            Object.keys(countries).map((c) => (
                              <MenuItem
                                key={countries[c]?.id}
                                value={countries[c]?.name}
                              >
                                {countries[c]?.name}
                              </MenuItem>
                            ))}
                        </FastField>
                      </Box>
                      <Box className="small-formik-select-fix">
                        <Field
                          component={Select}
                          label="Payments volume per month"
                          name="paymentsVolume"
                          size="small"
                          color="primary"
                        >
                          <MenuItem value={"none"}>
                            None, just getting started
                          </MenuItem>
                          <MenuItem value={"1-99"}>Less than $50,000</MenuItem>
                          <MenuItem value={"100-999"}>
                            $50,000 to $100,000
                          </MenuItem>
                          <MenuItem value={"100-999"}>
                            $100,000 to $1,000,000
                          </MenuItem>
                          <MenuItem value={"1000-4999"}>
                            $1,000,000-$10,000,000
                          </MenuItem>
                          <MenuItem value={"5000+"}>
                            More than $10,000,000
                          </MenuItem>
                        </Field>
                      </Box>
                      <Field
                        component={TextField}
                        label="Anything else"
                        name="message"
                        size="small"
                        multiline
                        minRows={3}
                        placeholder="Tell us more about your project, needs, and timeline"
                      />
                      <Box
                        sx={{
                          textAlign: "left",
                          margin: "16px 0",
                          display: "flex",
                          flexDirection: "row",
                        }}
                      >
                        <Box
                          sx={{
                            flex: "1 1 auto",
                            display: { xs: "none", md: "block" },
                          }}
                        />
                        <Button
                          color="primary"
                          variant="contained"
                          endIcon={<KeyboardArrowRightIcon />}
                          disabled={isSubmitting || !isValid}
                          onClick={submitForm}
                          sx={{
                            padding: "6px 24px",
                            width: "160px",
                            height: "38px",
                          }}
                        >
                          {!isLoading ? (
                            "Contact sales"
                          ) : (
                            <CircularProgress size={"19px"} />
                          )}
                        </Button>
                        <Box
                          sx={{
                            flex: "1 1 auto",
                            display: { xs: "block", md: "none" },
                          }}
                        />
                      </Box>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
          </Card>
        </Box>
        {isLoading && (
          <Box sx={{ textAlign: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}
      </>
    </div>
  );
};

export default ContactSales;
